import { useState } from 'react';
import { Plus, Users, ShieldCheck, ShieldAlert, Activity, Trash2, LockKeyhole } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { createProject, deleteProject, useProjectsPaginated, useWhoami, type Project } from '../api/client';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Skeleton } from '../components/ui/Skeleton';
import { Spinner } from '../components/ui/Spinner';
import { SlugInput, slugIsValid } from '../components/SlugInput';

const PROJECT_SKELETON_KEYS = Array.from({ length: 6 }, () => crypto.randomUUID());

function ProjectsTableBody({
  items,
  isLoading,
  noProjectAccess,
  onTrash,
}: Readonly<{
  items: Project[];
  isLoading: boolean;
  noProjectAccess: boolean;
  onTrash: (p: { id: string; name: string }) => void;
}>) {
  const { t } = useTranslation();
  if (isLoading && items.length === 0) {
    return (
      <>
        {PROJECT_SKELETON_KEYS.map((key) => (
          <tr key={key} className="border-b border-border/60 last:border-0">
            <td className="px-5 py-3"><Skeleton width="6rem" /></td>
            <td className="px-5 py-3"><Skeleton width="10rem" /></td>
            <td className="px-5 py-3"><Skeleton width="60%" /></td>
            <td className="px-5 py-3 text-right"><Skeleton width="14rem" height="1.75rem" rounded="0.375rem" /></td>
          </tr>
        ))}
      </>
    );
  }

  if (items.length) {
    return (
      <>
        {items.map((p) => (
          <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
            <td className="px-5 py-3 font-mono text-accent">{p.slug}</td>
            <td className="px-5 py-3 text-fg">{p.name}</td>
            <td className="px-5 py-3 text-fg-muted">{p.description}</td>
            <td className="px-5 py-3 text-right">
              <div className="inline-flex items-center gap-2">
                <Link
                  to={`/projects/${p.id}/license-compliance`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
                  title={t('projects.actions.licensesTitle')}
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {t('projects.actions.licenses')}
                </Link>
                <Link
                  to={`/projects/${p.id}/quarantine`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
                  title={t('projects.actions.quarantineTitle')}
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {t('projects.actions.quarantine')}
                </Link>
                <Link
                  to={`/projects/${p.id}/builds`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
                  title={t('projects.actions.buildsTitle')}
                >
                  <Activity className="h-3.5 w-3.5" />
                  {t('projects.actions.builds')}
                </Link>
                <Link
                  to={`/projects/${p.id}/members`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-border hover:bg-bg-hover text-fg-muted hover:text-fg"
                  title={t('projects.actions.membersTitle')}
                >
                  <Users className="h-3.5 w-3.5" />
                  {t('projects.actions.members')}
                </Link>
                <button
                  type="button"
                  onClick={() => onTrash(p)}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs border border-border text-fg-muted hover:bg-danger/10 hover:text-danger hover:border-danger/30"
                  title={t('projects.actions.trashTitle')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('projects.actions.trash')}
                </button>
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  }

  if (noProjectAccess) {
    return (
      <tr>
        <td colSpan={4} className="px-5 py-8">
          <div className="max-w-xl mx-auto flex flex-col items-center gap-3 text-center">
            <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
              <LockKeyhole className="h-5 w-5 text-accent" />
            </div>
            <p className="text-fg font-medium">{t('projects.noAccess.title')}</p>
            <p className="text-fg-muted text-sm leading-relaxed">
              <Trans
                i18nKey="projects.noAccess.body"
                components={[<span key={0} className="font-mono text-fg" />]}
              />
            </p>
            <a
              href="https://docs.orbitalreg.com/guide/concepts#who-sees-what"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-accent hover:underline"
            >
              docs.orbitalreg.com/guide/concepts#who-sees-what
            </a>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan={4} className="px-5 py-8 text-center text-fg-muted text-sm">
        {t('projects.table.empty')}
      </td>
    </tr>
  );
}

export function Projects() {
  const { t } = useTranslation();
  // Item 97 Phase C: cursor-paginated list. `items` accumulates across
  // pages as the user clicks "Show more"; `hasMore` flips false on the
  // last page so the button hides itself without an extra round-trip.
  const { items, error, isLoading, isValidating, hasMore, loadMore, mutate } =
    useProjectsPaginated();
  const { user } = useWhoami();
  // Item 139: a config-mapped reader/developer role grants no
  // role_bindings row by itself, so a freshly-SSO'd user can see 0
  // projects with no indication why. Distinguish that case from
  // "no projects created yet" (where hasAnyBinding is true/undefined,
  // e.g. org_admin) so we don't misdescribe an empty-but-authorized
  // workspace as a permissions problem.
  const noProjectAccess = user?.hasAnyBinding === false;
  const [form, setForm] = useState({ slug: '', name: '', description: '' });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createProject(form);
      setForm({ slug: '', name: '', description: '' });
      await mutate();
    } finally {
      setBusy(false);
    }
  };

  // Move a project (and its repos) to the 30-day trash. Backend enforces
  // project_admin and 403s otherwise.
  const onTrash = async (p: { id: string; name: string }) => {
    if (!confirm(t('projects.confirmTrash', { name: p.name }))) return;
    try {
      await deleteProject(p.id);
      await mutate();
    } catch (e) {
      alert(t('projects.trashError', { message: e instanceof Error ? e.message : String(e) }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="orb-eyebrow text-accent/80 mb-2">{t('projects.eyebrow')}</p>
          <h1 className="text-2xl font-display font-semibold text-fg leading-tight">{t('projects.title')}</h1>
          <p className="text-fg-muted text-sm mt-2">
            {t('projects.intro')}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader
          title={t('projects.create.title')}
          description={t('projects.create.description')}
        />
        <CardBody>
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
            <SlugInput
              value={form.slug}
              onChange={(slug) => setForm({ ...form, slug })}
              required
            />
            <Input
              placeholder={t('projects.create.namePlaceholder')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              placeholder={t('projects.create.descriptionPlaceholder')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="md:col-span-1"
            />
            <Button variant="primary" type="submit" disabled={busy || !slugIsValid(form.slug)}>
              <Plus className="h-4 w-4" />
              {t('projects.create.submit')}
            </Button>
          </form>
        </CardBody>
      </Card>

      {error && (
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{String(error)}</p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title={t('projects.table.title')} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">{t('projects.table.slug')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('projects.table.name')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('projects.table.description')}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              <ProjectsTableBody
                items={items}
                isLoading={isLoading}
                noProjectAccess={noProjectAccess}
                onTrash={onTrash}
              />
            </tbody>
          </table>
        </div>
        {hasMore && (
          <CardBody>
            <div className="flex justify-center">
              <Button
                variant="ghost"
                onClick={loadMore}
                disabled={isValidating}
              >
                {isValidating ? <Spinner size="xs" /> : t('projects.table.showMore')}
              </Button>
            </div>
          </CardBody>
        )}
      </Card>
    </div>
  );
}
